import {
  getPlanLimits,
  isAtLimit,
  isPlanLimitCode,
  PLAN_LIMIT_CODES,
  type PlanId,
} from "@repo/shared/billing/plans";
import { planFromSubscription } from "@repo/shared/billing/subscription";

import { connections$ } from "~/stores/connections";
import { type Datasource, datasources$ } from "~/stores/datasources";
import { subscription$ } from "./subscription.store";

/**
 * The org's effective billing plan, derived from `subscription$`. An org with
 * no live subscription (or a lapsed one) is on the implicit `"free"` plan.
 * Resolved through the same shared helper the server uses, so the two can't
 * disagree about what a row entitles an org to.
 *
 * Call inside `useValue(() => ...)` for reactive reads.
 */
export const getOrgPlan = (): PlanId =>
  planFromSubscription(subscription$.get());

/** Count the org's user-connected datasources (excludes managed + sample). */
export const countBillableDatasources = (): number => {
  const items = datasources$.get() ?? {};
  return Object.values(items).filter((d) => {
    const ds = d as Datasource | undefined;
    return ds && !ds.managed && !ds.isSample;
  }).length;
};

/** Count the org's Fivetran-backed connections. */
export const countFivetranConnections = (): number =>
  Object.keys(connections$.get() ?? {}).length;

/** Whether the org has hit its plan's datasource cap. Unlimited plans never do. */
export const isDatasourceLimitReached = (): boolean => {
  const limit = getPlanLimits(getOrgPlan()).datasources;
  return isAtLimit(countBillableDatasources(), limit);
};

/** Whether the org has hit its plan's Fivetran-connection cap (free = 0). */
export const isFivetranLimitReached = (): boolean => {
  const limit = getPlanLimits(getOrgPlan()).fivetranConnections;
  return isAtLimit(countFivetranConnections(), limit);
};

/** Reasons the upgrade prompt can be opened for. */
export type UpgradeReason = "datasources" | "fivetran" | "general";

/**
 * Map a server error to the plan-limit reason it represents, or `null` if it
 * isn't a plan-limit rejection. Server guards throw `FORBIDDEN` with a
 * `PLAN_LIMIT_*` token as the message (see `@repo/shared/billing/plans`).
 */
export const planLimitReasonFromError = (
  err: unknown,
): UpgradeReason | null => {
  const message = err instanceof Error ? err.message : "";
  if (message.includes(PLAN_LIMIT_CODES.datasources)) return "datasources";
  if (message.includes(PLAN_LIMIT_CODES.fivetranConnections)) return "fivetran";
  // Some tRPC client links prefix the path; fall back to a token scan.
  if (isPlanLimitCode(message.trim())) {
    return message.includes("FIVETRAN") ? "fivetran" : "datasources";
  }
  return null;
};
